// src/components/CarteBordFrise.jsx — LE LANGAGE PARTAGÉ DES FRISES (Suivi
// équipe + Demandes d'horaires). Direction visuelle validée Dimitri
// 2026-08-16 : LA RÈGLE DE COULEUR — tout ce qui est gris-bleu #3d5166
// appartient au VOYAGEUR ; le bleu ciel et les verts au MÉNAGE ; le
// terracotta à l'ALERTE.
//
// LA CARTE DU VOYAGEUR occupe TOUTE SA ZONE (plus de zone grise séparée) :
// fond #3d5166 plein, texte blanc calé CONTRE LE MÉNAGE (à droite pour le
// partant, à gauche pour l'arrivant), arrondi 5 px côté bord / 4 px côté
// intérieur. Les deux côtés portent leur heure ; le nombre de personnes vit à
// GAUCHE (les runs naissent de la résa qui SE TERMINE : `guest_count` est le
// nombre du PARTANT — l'arrivant, lui, n'a pas de nombre connu ici).
// Dégradation quand la zone rétrécit : le prénom tombe
// d'abord, puis le nombre — L'HEURE RESTE ; icône seule sous ~41 px
// (`contenuCarteBord`, lib pure, mesure de texte au canvas).
import React from 'react'
import { LogOut, LogIn } from 'lucide-react'
import { contenuCarteBord } from '../lib/menage-frise'

// ⚠️ UNE SEULE VALEUR POUR TOUTE L'APPLICATION (règle « un dessin par
// information », roadmap 2026-08-16).
export const FOND_PISTE_FRISE = '#f6f8fa'
export const CARTE_VOYAGEUR_FOND = '#3d5166'
export const PLAGE_FOND = '#fff'
export const PLAGE_BORD = '#dbe3ea'
export const BLOC_MENAGE_FOND = '#5c8ab8'
export const BLOC_VALIDE = { fond: '#7fc4a3', lisere: '#1a4f3a', texte: '#124a33' }
export const BLOC_RETARD = { fond: '#f0c4b6', lisere: '#b8412b', texte: '#8f2f1d' }
export const AVANCEMENT_FOND = 'rgba(61,81,102,.16)'
export const AVANCEMENT_PLEIN = '#233240'
// Une demande PROLONGE LE SÉJOUR du voyageur : hachures GRIS-BLEU (en bleu,
// elles se confondaient avec le ménage).
// Un cran plus fortes (version finale) — les rayures ne disent plus qu'UNE
// chose : un voyageur réclame ce temps. Collées à sa carte.
export const HACHURE_DEMANDE_FRISE =
  'repeating-linear-gradient(45deg, rgba(61,81,102,.5) 0 5px, rgba(61,81,102,.16) 5px 10px)'
export const HACHURE_CONTOUR = 'rgba(61,81,102,.42)'
export const HORLOGE_DEMANDE_FOND = '#3d5166'
export const REPERE_CONTRAT_TRAIT = '#8b96a3'
export const ANNONCE_CERCLE_BORD = '#1f5a43'

let _cv = null
export function mesureTexteFrise(texte, px) {
  if (!_cv) {
    try { _cv = document.createElement('canvas').getContext('2d') } catch (_) { return texte.length * 6 }
  }
  if (!_cv) return texte.length * 6
  _cv.font = `${px}px "Libre Franklin", sans-serif`
  return Math.ceil(_cv.measureText(texte).width)
}

/**
 * La carte du voyageur — pleine zone. `pctBord` = l'heure de départ (gauche)
 * ou d'arrivée (droite) en % d'axe. `echelle` : 'suivi' (texte 8.5, icône 9)
 * ou 'demandes' (texte 9.5, icône 11).
 */
export default function CarteBordFrise({
  cote, prenom, personnes, heure, pctBord, largeurZonePx, echelle = 'suivi', testId,
  colleInterieur = false, alerte = false, hauteurPiste = null,
}) {
  // ⚠️ MÊME HAUTEUR que les hachures (correction Dimitri 2026-08-16) : carte
  // de départ, carte d'arrivée et hachures — aucune plus haute qu'une autre,
  // c'est ce qui fait le bloc continu moitié plein moitié rayé.
  const dims = echelle === 'demandes'
    ? { texte: 9.5, icone: 11, marge: 4 }
    : { texte: 8.5, icone: 9, marge: 3 }
  const { mode, texte } = contenuCarteBord({
    prenom, personnes, heure, largeurZonePx,
    mesureTexte: (t) => mesureTexteFrise(t, dims.texte),
    iconePx: dims.icone,
  })
  const Icone = cote === 'gauche' ? LogOut : LogIn
  return (
    <span data-testid={testId} title={texte || undefined}
      style={{
        // ⚠️ HAUTEUR FIXE (mesure 2026-08-16) : quand la piste s'agrandit pour
        // le libellé « contrat 16h », la carte ne s'étire PAS avec elle — elle
        // recouvrait le libellé et semblait énorme.
        position: 'absolute', top: dims.marge,
        ...(Number.isFinite(hauteurPiste)
          ? { height: hauteurPiste - dims.marge * 2 }
          : { bottom: dims.marge }),
        ...(cote === 'gauche'
          ? { left: 0, width: `${pctBord}%`, justifyContent: mode === 'icone' ? 'center' : 'flex-end',
              // Collée aux hachures : le côté intérieur perd son arrondi — la
              // carte et les rayures forment UN bloc, moitié plein moitié rayé.
              borderRadius: colleInterieur ? '5px 0 0 5px' : '5px 4px 4px 5px' }
          : { left: `${pctBord}%`, width: `${Math.max(0, 100 - pctBord)}%`,
              justifyContent: mode === 'icone' ? 'center' : 'flex-start',
              borderRadius: colleInterieur ? '0 5px 5px 0' : '4px 5px 5px 4px' }),
        display: 'inline-flex', alignItems: 'center', gap: 4,
        // ⚠️ ALERTE (voyageur ARRIVÉ pendant un ménage non fini) : LA MÊME
        // carte passe en terracotta — un seul objet, jamais un trait doublon
        // (correction Dimitri 2026-08-16).
        padding: '0 5px', background: alerte ? '#b8412b' : CARTE_VOYAGEUR_FOND,
        boxSizing: 'border-box', whiteSpace: 'nowrap', overflow: 'hidden',
        fontSize: dims.texte, color: '#fff', fontWeight: 600,
        fontFamily: '"Libre Franklin", sans-serif',
        zIndex: 2, pointerEvents: 'none',
      }}>
      <Icone size={dims.icone} strokeWidth={2.2} color="rgba(255,255,255,.66)" style={{ flexShrink: 0 }} />
      {mode === 'texte' ? texte : null}
    </span>
  )
}
